import {
  Box,
  Card,
  Flex,
  Grid,
  Text,
  Button,
  Heading,
  CardBody,
  useToast,
  CardHeader,
  LinkButton,
  LoadingSpinner
} from '@homebotapp/hb-react-component-catalog'
import {
  DEFAULT_LOAN_TERM,
  DEFAULT_CREDIT_SCORE,
  CREDIT_SCORE_VALUE_TO_RANGES
} from '../../../Components/homeSearch/ListingDetails/EstimatedMonthlyPayment/Utilities/EstimatedMonthlyPaymentConstants'
import { match } from 'react-router'
import history from '../../../history'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { hasCustomerToken, isAuthTokenSupported } from '../../../auth'
import { BuyerInfo } from '../../../types/buyerInfo'
import { useIntl, defineMessages } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { AppLayout } from '../../../App/Layout/AppLayout'
import { useGetClientId } from '../../../hooks/useGetClientId'
import { SEARCH_PATH_HELPERS } from '../../../constants/navigation'
import { formatAbbrCurrency, formatCurrency } from '../../../helpers/formatters'
import { fetchMortgageRates } from '../../../actions/mortgageRates'
import { selectBuyerInfo } from '../../../store/selectors/buyerInfo'
import { ClientEventSource, ClientEventName, FeedType } from '../../../api/gqlaxy/gql/generated/graphql'
import BotHead from '../../../Components/shared/Avatar/BotHeadColored'
import { UPDATE_BUYER_INFO_SUCCESS } from '../../../actions/actionTypes'
import { useUpdateClient } from '../../../hooks/listings/useUpdateClient'
import { useGetZipCodeData } from '../../../hooks/listings/useGetZipCodeData'
import { selectSponsorshipPhotoUri } from '../../../store/selectors/sponsorship'
import { selectCustomerId, selectCustomerPhotoUri } from '../../../store/selectors/customerProfile'
import { selectLatestMortgageRates } from '../../../store/selectors/mortgageRate'
import { ListingCardNew } from '../../../Components/homeSearch/ListingCardNew/ListingCardNew'
import { useGetSingleFeedForZipCode } from '../../../hooks/gallery/useGetSingleFeedForZipCode'
import { AffordabilityCalculatorOtherCostsSection } from './AffordabilityCalculatorOtherCostsSection'
import { AffordabilityCalculatorLoanDetailsSection } from './AffordabilityCalculatorLoanDetailsSection'
import {
  AffordabilityCalculatorDebtToIncomeSection,
  DEBT_TO_INCOME_DEFAULT
} from './AffordabilityCalculatorDebtToIncomeSection'
import { AffordabilityCalculatorYourInformationSection } from './AffordabilityCalculatorYourInformationSection'
import { getRate } from '../../../Components/homeSearch/ListingDetails/EstimatedMonthlyPayment/Utilities/EstimatedMonthlyPaymentUtilities'
import { EstimatedMonthlyPaymentBreakdownCustomerDM } from '../../../Components/homeSearch/ListingDetails/EstimatedMonthlyPayment/EstimatedMonthlyPaymentBreakdown/EstimatedMonthlyPaymentBreakdownCustomerDM/EstimatedMonthlyPaymentBreakdownCustomerDM'
import { PATH_HELPERS } from '../../../constants/navigation'
import { constructFiltersAndOmitEmpty } from '../../../helpers/listings/filters'
import { useCreateClientEvent } from '../../../hooks/useCreateClientEvent'
import { useTrackingContext } from '../../../providers/tracking'
import { HomeSearchFooter } from '../../../Components/homeSearch/HomeSearchFooter/HomeSearchFooter'

export const MSG = defineMessages({
  viewAllCalculators: {
    id: 'AffordabilityCalculator.viewAllCalculators',
    defaultMessage: 'View all calculators'
  },
  header: {
    id: 'AffordabilityCalculator.header',
    defaultMessage: 'Estimate what you can afford'
  },
  getPrequalified: {
    id: 'AffordabilityCalculator.getPrequalified',
    defaultMessage: 'Confirm with a pre-qual'
  },
  setYourBudget: {
    id: 'AffordabilityCalculator.setYourBudget',
    defaultMessage: 'Set your budget to {homePrice}'
  },
  budgetUpdateSuccess: {
    id: 'AffordabilityCalculator.budgetUpdateSuccess',
    defaultMessage: 'Budget updated successfully'
  },
  budgetUpdateError: {
    id: 'AffordabilityCalculator.budgetUpdateError',
    defaultMessage: 'There was an error updating your budget. Please try again.'
  },
  done: {
    id: 'AffordabilityCalculator.done',
    defaultMessage: 'Done!'
  },
  oops: {
    id: 'AffordabilityCalculator.oops',
    defaultMessage: 'Oops!'
  },
  listings: {
    id: 'AffordabilityCalculator.listings',
    defaultMessage: 'Browse homes around {price} in {zipCode}'
  },
  viewAll: {
    id: 'AffordabilityCalculator.viewAll',
    defaultMessage: 'View all'
  },
  loadingListings: {
    id: 'AffordabilityCalculator.loadingListings',
    defaultMessage: 'Loading listings...'
  },
  noListings: {
    id: 'AffordabilityCalculator.noListings',
    defaultMessage: 'No listings found around your budget in this area'
  },
  currentBudget: {
    id: 'AffordabilityCalculator.currentBudget',
    defaultMessage: 'Your budget is currently set to {budget}'
  }
})

export interface AffordabilityCalculatorProps {
  match: match<{ buyerId?: string }>
}

export const AffordabilityCalculator = ({ match }: AffordabilityCalculatorProps) => {
  const intl = useIntl()
  const toast = useToast()
  const dispatch = useDispatch()
  const clientId = useGetClientId()
  const isImpersonating = hasCustomerToken()
  const { trackingClient } = useTrackingContext()
  const customerId = useSelector(selectCustomerId)
  const rates = useSelector(selectLatestMortgageRates)
  const buyerInfo: BuyerInfo = useSelector(selectBuyerInfo)
  const { mutateAsync: updateClientInfo } = useUpdateClient()
  const [loanTerm, setLoanTerm] = useState(DEFAULT_LOAN_TERM)
  const [hoa, setHoa] = useState(localStorage?.getItem('affordabilityCalculatorHoa') || '')
  const [pmi, setPmi] = useState(localStorage?.getItem('affordabilityCalculatorPmi') || '')
  const [zipCode, setZipCode] = useState(localStorage?.getItem('affordabilityCalculatorZipCode') || '')
  const [loanType, setLoanType] = useState(localStorage?.getItem('affordabilityCalculatorLoanType') || '')
  const [insurance, setInsurance] = useState(localStorage?.getItem('affordabilityCalculatorHomeInsurance') || '')
  const [propertyTax, setPropertyTax] = useState(localStorage?.getItem('affordabilityCalculatorPropertyTax') || '')
  const [monthlyDebt, setMonthlyDebt] = useState(localStorage?.getItem('affordabilityCalculatorMonthlyDebt') || '')
  const [totalMonthlyExpenses, setTotalMonthlyExpenses] = useState('')
  const [downPayment, setDownPayment] = useState(localStorage?.getItem('affordabilityCalculatorDownPayment') || '')
  const [yearlyIncome, setYearlyIncome] = useState(localStorage?.getItem('affordabilityCalculatorYearlyIncome') || '')
  const [creditScore, setCreditScore] = useState(
    localStorage?.getItem('creditScore') || DEFAULT_CREDIT_SCORE.toString()
  )
  const rate = getRate(rates?.[loanTerm], Number(creditScore))
  const [interestRate, setInterestRate] = useState(rate.toFixed(3))
  const [isSliding, setIsSliding] = useState(false)
  const [homePrice, setHomePrice] = useState(0)
  const [monthlyPayment, setMonthlyPayment] = useState(0)
  const { data: zipCodeData } = useGetZipCodeData(
    {
      input: { zipCode, homePrice: Number(homePrice) }
    },
    { enabled: isAuthTokenSupported() && zipCode?.length === 5 && !isSliding && Number(homePrice) > 0 }
  )
  const { data: listingsData, isFetching } = useGetSingleFeedForZipCode(
    { input: { zipCode, feedType: FeedType.NewlyListed, limit: 10, priceAnchor: Math.round(Number(homePrice)) } },
    { enabled: isAuthTokenSupported() && zipCode?.length === 5 && !isSliding && Number(homePrice) > 0 }
  )
  const listings = listingsData?.ephemeralFeeds?.[0]?.listings

  const [debtToIncomeRatio, setDebtToIncomeRatio] = useState(
    Number(localStorage?.getItem('affordabilityCalculatorDebtToIncome')) || DEBT_TO_INCOME_DEFAULT
  )

  const [hasUsedAffordabilityCalculator, setHasUsedAffordabilityCalculator] = useState(false)

  const { mutateAsync: createClientEvent } = useCreateClientEvent({ disableErrorPrompts: true })

  const createUsedAffordabilityCalculatorEvent = useCallback(() => {
    createClientEvent({
      clientEventInput: {
        eventName: ClientEventName.UsedAffordabilityCalculator,
        eventSource: ClientEventSource.Calculators
      }
    })
  }, [createClientEvent])

  useEffect(() => {
    if (hasUsedAffordabilityCalculator) {
      createUsedAffordabilityCalculatorEvent()
      trackingClient?.clientUsedAffordabilityCalculator({ pathname: window.location.pathname })
    }
  }, [hasUsedAffordabilityCalculator])

  useEffect(() => {
    dispatch(fetchMortgageRates())
  }, [dispatch])

  useEffect(() => {
    setInterestRate(rate.toFixed(3))
  }, [rate])

  useEffect(() => {
    if (
      zipCodeData?.zipCodeData?.monthlyHomeInsurance &&
      zipCodeData?.zipCodeData?.monthlyHomeInsurance > 0 &&
      Math.floor(Number(zipCodeData?.zipCodeData?.monthlyHomeInsurance)) !== Math.floor(Number(insurance))
    ) {
      setInsurance(zipCodeData.zipCodeData.monthlyHomeInsurance.toString())
      localStorage?.setItem(
        'affordabilityCalculatorHomeInsurance',
        zipCodeData.zipCodeData.monthlyHomeInsurance.toString()
      )
    }

    if (
      zipCodeData?.zipCodeData?.monthlyPropertyTaxes &&
      zipCodeData?.zipCodeData?.monthlyPropertyTaxes > 0 &&
      Math.floor(Number(zipCodeData?.zipCodeData?.monthlyPropertyTaxes) * 12) !== Math.floor(Number(propertyTax))
    ) {
      setPropertyTax((zipCodeData.zipCodeData.monthlyPropertyTaxes * 12).toString())
      localStorage?.setItem(
        'affordabilityCalculatorPropertyTax',
        (zipCodeData.zipCodeData.monthlyPropertyTaxes * 12).toString()
      )
    }
  }, [zipCodeData])

  useEffect(() => {
    setTotalMonthlyExpenses(
      (Number(monthlyDebt) + Number(insurance) + Number(propertyTax) / 12 + Number(hoa) + Number(pmi)).toString()
    )
  }, [monthlyDebt, insurance, propertyTax, hoa, pmi])

  const handleUpdateBudget = async () => {
    const result = await updateClientInfo({
      input: {
        affordability: Math.round(Number(homePrice))
      }
    })

    if (result.updateClient.updatedAt) {
      dispatch({
        type: UPDATE_BUYER_INFO_SUCCESS,
        data: {
          ...buyerInfo,
          affordability: Math.round(Number(homePrice))
        }
      })

      toast({
        title: intl.formatMessage(MSG.done),
        description: intl.formatMessage(MSG.budgetUpdateSuccess),
        status: 'success',
        isClosable: true
      })
    } else {
      toast({
        title: intl.formatMessage(MSG.oops),
        description: intl.formatMessage(MSG.budgetUpdateError),
        status: 'error',
        isClosable: true
      })
    }
  }

  const customPrequalMessage = useMemo(() => {
    let message = ''

    if (Number(homePrice) > 0) {
      message += `I used the affordability calculator and I want to get pre-qualified for ${formatCurrency(
        Number(homePrice)
      )}.`
    } else {
      message +=
        'The affordability calculator has calculated that my expenses are likely too high for my current income to be able to afford a home at the moment.'
    }

    if (Number(yearlyIncome) > 0) {
      message += ` I have a yearly income of ${formatCurrency(Number(yearlyIncome))},`
    }

    if (Number(creditScore) > 0) {
      message += ` a credit score of ${CREDIT_SCORE_VALUE_TO_RANGES[creditScore]},`
    }

    if (Number(downPayment) > 0) {
      message += ` a down payment of ${formatCurrency(Number(downPayment))} saved up,`
    }

    if (Number(monthlyDebt) > 0) {
      message += ` ${formatCurrency(Number(monthlyDebt))} in monthly expenses,`
    }

    if (Number(homePrice) <= 0) {
      message += ' and I would like to chat with you about my options.'
    } else {
      if (Number(interestRate) > 0 && Number(homePrice) > 0) {
        message += ` a loan term of ${loanTerm} years, an interest rate of ${interestRate}%,`
      }

      if (Number(hoa) > 0) {
        message += ` ${formatCurrency(Number(hoa))} in monthly HOA fees,`
      }

      if (Number(pmi) > 0) {
        message += ` ${formatCurrency(Number(pmi))} in monthly PMI,`
      }

      if (Number(insurance) > 0) {
        message += ` ${formatCurrency(Number(insurance))} in monthly home insurance,`
      }

      if (Number(propertyTax) > 0) {
        message += ` ${formatCurrency(Number(propertyTax) / 12)} in monthly property taxes,`
      }

      if (Number(debtToIncomeRatio) > 0) {
        message += ` and this all adds up to a ${debtToIncomeRatio}% debt-to-income ratio.`
      }
    }

    return message
  }, [
    homePrice,
    yearlyIncome,
    creditScore,
    downPayment,
    monthlyDebt,
    interestRate,
    loanTerm,
    hoa,
    pmi,
    insurance,
    propertyTax,
    debtToIncomeRatio
  ])

  return (
    <AppLayout loading={false} enableAgentDropdown match={match}>
      <Box mb={24}>
        <Box px={[5, null, null, 20]} py={[5, null, null, 10]}>
          <Flex mx='auto' maxWidth='1400px' direction='column' gap={4}>
            <Heading as='h1' size={['md', null, null, 'lg']} textAlign='left'>
              {intl.formatMessage(MSG.header)}
            </Heading>
            <Flex width='100%' direction={['column', null, null, 'row']} gap={8}>
              <Flex width={['100%', null, null, '50%']} direction='column' gap={3}>
                <AffordabilityCalculatorYourInformationSection
                  zipCode={zipCode}
                  setZipCode={setZipCode}
                  creditScore={creditScore}
                  monthlyDebt={monthlyDebt}
                  downPayment={downPayment}
                  yearlyIncome={yearlyIncome}
                  setCreditScore={setCreditScore}
                  setMonthlyDebt={setMonthlyDebt}
                  setDownPayment={setDownPayment}
                  setYearlyIncome={setYearlyIncome}
                  setHasUsedAffordabilityCalculator={setHasUsedAffordabilityCalculator}
                />
                <AffordabilityCalculatorLoanDetailsSection
                  loanType={loanType}
                  setLoanType={setLoanType}
                  setLoanTerm={setLoanTerm}
                  interestRate={interestRate}
                  setInterestRate={setInterestRate}
                  setHasUsedAffordabilityCalculator={setHasUsedAffordabilityCalculator}
                />
                <AffordabilityCalculatorOtherCostsSection
                  hoa={hoa}
                  pmi={pmi}
                  setHoa={setHoa}
                  setPmi={setPmi}
                  insurance={insurance}
                  setZipCode={setZipCode}
                  propertyTax={propertyTax}
                  setInsurance={setInsurance}
                  setPropertyTax={setPropertyTax}
                  setHasUsedAffordabilityCalculator={setHasUsedAffordabilityCalculator}
                />
              </Flex>
              <Flex width={['100%', null, null, '50%']} direction='column' gap={4}>
                <AffordabilityCalculatorDebtToIncomeSection
                  loanTerm={loanTerm}
                  downPayment={downPayment}
                  setIsSliding={setIsSliding}
                  yearlyIncome={yearlyIncome}
                  interestRate={interestRate}
                  setHomePrice={setHomePrice}
                  setMonthlyPayment={setMonthlyPayment}
                  totalMonthlyExpenses={totalMonthlyExpenses}
                  setDebtToIncomeRatio={setDebtToIncomeRatio}
                  setHasUsedAffordabilityCalculator={setHasUsedAffordabilityCalculator}
                />

                {!isImpersonating && Number(homePrice) > 0 && (
                  <Card p={4}>
                    <CardBody p={0}>
                      <Text>
                        {intl.formatMessage(MSG.currentBudget, {
                          budget: <b>{formatCurrency(buyerInfo.affordability ?? 0)}</b>
                        })}
                      </Text>

                      <Button
                        width='100%'
                        onClick={handleUpdateBudget}
                        tracking={{
                          guid: 'pxhUU9PR75Wjxp5ShEqkaZ',
                          descriptor: 'Set your budget',
                          ui_context: 'Affordability Calculator Page'
                        }}
                      >
                        {intl.formatMessage(MSG.setYourBudget, { homePrice: formatCurrency(Math.round(homePrice)) })}
                      </Button>
                    </CardBody>
                  </Card>
                )}
                <EstimatedMonthlyPaymentBreakdownCustomerDM
                  buyerId={match.params.buyerId}
                  showModal={Number(homePrice) <= 0}
                  customMessage={customPrequalMessage}
                  tracking={{
                    guid: '3MDVgw5p2Vcu1qHW4hbY8J',
                    ui_context: 'Affordability Calculator Page',
                    descriptor: 'Get pre-qualified'
                  }}
                />
              </Flex>
            </Flex>
            {Number(homePrice) > 0 && Number(monthlyPayment) > 0 && zipCode?.length === 5 && (
              <>
                <Flex
                  my={2}
                  gap={2}
                  direction={['column', null, null, 'row']}
                  alignItems={['flex-start', null, null, 'center']}
                >
                  <Heading mb={0} as='h3' size={['sm', null, null, 'md']} textAlign='left'>
                    {intl.formatMessage(MSG.listings, { price: formatAbbrCurrency(homePrice), zipCode })}
                  </Heading>
                  <LinkButton
                    size='xs'
                    variant='ghost'
                    colorScheme='primary'
                    onClick={() =>
                      history.push(
                        PATH_HELPERS.listings.buildPath(
                          customerId,
                          'postal_code',
                          zipCode,
                          constructFiltersAndOmitEmpty({
                            minPrice: Number(homePrice) * 0.8,
                            maxPrice: Number(homePrice) * 1.2
                          })
                        )
                      )
                    }
                  >
                    {intl.formatMessage(MSG.viewAll)}
                  </LinkButton>
                </Flex>
                <Flex flex='1' overflowX='auto' overflowY='hidden'>
                  {isFetching || isSliding ? (
                    <Flex my={8} direction='column' justifyContent='center' alignItems='center' width='100%'>
                      <LoadingSpinner variant='bot-head' size='lg' />
                      <Text mt={2} color='neutral.900' size='lg'>
                        {intl.formatMessage(MSG.loadingListings)}
                      </Text>
                    </Flex>
                  ) : listings?.length > 0 ? (
                    <Grid gridAutoFlow='column' gridAutoColumns='280px' gap={4} minWidth='max-content'>
                      {listings?.map(listing => (
                        <ListingCardNew
                          key={listing.id}
                          listing={listing}
                          clientId={clientId}
                          tracking={{
                            guid: 'nJ3p6VcRnvzUgVfhG7ts68',
                            ui_context: 'Affordability Calculator Page',
                            descriptor: 'Listing Clicked'
                          }}
                        />
                      ))}
                    </Grid>
                  ) : (
                    <Card
                      pt={8}
                      pb={4}
                      px={4}
                      width='100%'
                      display='flex'
                      alignItems='center'
                      flexDirection='column'
                      justifyContent='center'
                    >
                      <BotHead quizzical type='error' size='l' />
                      <Text textAlign='center' mt={2} color='neutral.900' size='lg'>
                        {intl.formatMessage(MSG.noListings)}
                      </Text>
                    </Card>
                  )}
                </Flex>
              </>
            )}
          </Flex>
        </Box>
        <Flex>
          <LinkButton
            mb={4}
            mx='auto'
            flex='0 0 auto'
            size={['sm', null, null, 'md']}
            onClick={() => history.push(SEARCH_PATH_HELPERS.calculators.buildPath(match.params.buyerId))}
            tracking={{
              guid: '4v8BbfMrGmVowewNnMhM34',
              descriptor: 'View all calculators',
              ui_context: 'Affordability Calculator Page'
            }}
          >
            {intl.formatMessage(MSG.viewAllCalculators)}
          </LinkButton>
        </Flex>
      </Box>
      <HomeSearchFooter />
    </AppLayout>
  )
}
